import type {
  SavingsCustomerIndividual,
  SavingsCustomerIndividualCreatePayload,
  SavingsCustomerIndividualUpdatePayload
} from '@/interfaces/SavingsCustomer.interface';
import { filenameFromContentDisposition } from '../savings-products/response-helpers';
import {
  SAVINGS_API_VERSION_HEADERS,
  SavingsReferenceResourceClient
} from '../savings-reference-resource';
import type { ListExportParams } from '../types';

export class ApiSavingsCustomerIndividuals extends SavingsReferenceResourceClient<
  SavingsCustomerIndividual,
  SavingsCustomerIndividualCreatePayload
> {
  protected readonly path = '/savings-customer-individuals';
  protected readonly searchFilter = 'search';

  async get(id: number): Promise<SavingsCustomerIndividual> {
    const res = await this.client.get(`${this.path}/${id}`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async updateRoles(
    id: number,
    payload: SavingsCustomerIndividualUpdatePayload
  ): Promise<SavingsCustomerIndividual> {
    const res = await this.client.patch(`${this.path}/${id}`, payload, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  /** CSV of every link the list's filters, search and sort select. */
  async export(
    listData: ListExportParams,
    searchTerm?: string
  ): Promise<{ blob: Blob; filename: string }> {
    const search = searchTerm?.trim();
    const response = await this.client.getList(
      `${this.path}/export`,
      {
        filters: {
          ...listData.filters,
          ...(search ? { [this.searchFilter]: search } : {})
        },
        sort: listData.sort
      },
      {
        responseType: 'blob',
        headers: { ...SAVINGS_API_VERSION_HEADERS, Accept: 'text/csv' }
      }
    );

    return {
      blob: new Blob([response.data], { type: 'text/csv;charset=utf-8' }),
      filename: filenameFromContentDisposition(
        response.headers['content-disposition'],
        'savings-individuals.csv'
      )
    };
  }
}

export { savingsCustomerIndividualsQueryKeys } from './query-keys';
export { useSavingsCustomerIndividualsListQuery } from './use-savings-customer-individuals-queries';
export {
  useSavingsCustomerIndividualsCreateMutation,
  useSavingsCustomerIndividualsDeleteMutation,
  useSavingsCustomerIndividualsExportMutation,
  useSavingsCustomerIndividualsUpdateMutation
} from './use-savings-customer-individuals-mutations';
export type {
  SavingsCustomerIndividualsExportVariables,
  SavingsCustomerIndividualsUpdateVariables
} from './use-savings-customer-individuals-mutations';
